import { Injectable } from '@angular/core';
import { Observable, Subscriber } from 'rxjs';
import {
    GemviewAttribute,
    GemviewElement,
    GemviewEvent,
    GemviewMessage,
    GemviewMessageType,
    GemviewResponse,
    GemviewSignResponse,
    METADATA
} from '../models/gemview.model';
import { GemviewMessagingService } from '../services/gemview/gemview-messaging.service';

/**
 * Service used to interact with the Gemview SDK and Topaz tablet.
 *
 * This is mainly achieved by setting custom DOM elements, attributes, and events which are
 * handled by the SigPlusExtLite browser extension.
 *
 * Topaz SigPlusExtLite SDK Integration Guide - see: https://www.topazsystems.com/software/SigPlusExtLite.pdf
 */
@Injectable()
export abstract class BaseGemviewService {
    /**
     * The tablet window currently being used with the service, if any.
     */
    protected tabletWindow: Window;

    /**
     * Array of gemview response types and their current subscriber, if any.
     */
    private subscribers: Array<{ type: string; subscriber: Subscriber<any> | null }> = [];

    /**
     * Initializes a new instance of the base gemview service.
     */
    constructor(protected gemviewMessagingService: GemviewMessagingService) { }

    public ngOnDestroy(): void {
        console.log('Base gemview ng on destroy!!!!');
    }

    /**
     * Dispatches the specified event to the DOM for handling by the Gemview SDK.
     *
     * @param event - The event to dispatch.
     * @param element - The element to create in the DOM.
     * @param attribute - The attribute to set on the element.
     * @param payload - The value to set for the attribute.
     */
    private dispatch = (event: GemviewEvent, element: GemviewElement, attribute: GemviewAttribute, payload: object): void => {
        const node = document.createElement(element);
        node.setAttribute(attribute, JSON.stringify(payload));
        document.documentElement.appendChild(node);
        node.dispatchEvent(new Event(event, METADATA.EVENT_INIT_OPTIONS));
    };

    /**
     * Dispatches the DeviceStatus event.
     */
    private dispatchGetDeviceStatus = (): void => {
        this.dispatch(
            GemviewEvent.DeviceStatus,
            GemviewElement.DeviceStatus,
            GemviewAttribute.DeviceStatus,
            METADATA.DEVICE_STATUS,
        );
    };

    /**
     * Dispatches the CustomWindow event.
     */
    private dispatchCustomSigningWindow = (): void => {
        this.dispatch(
            GemviewEvent.CustomWindow,
            GemviewElement.CustomWindow,
            GemviewAttribute.CustomWindow,
            METADATA.CUSTOM_WINDOW,
        );
    };

    /**
     * Dispatches the PushTab event.
     */
    private dispatchPushCurrentTab = (): void => {
        this.dispatch(
            GemviewEvent.PushTab,
            GemviewElement.PushTab,
            GemviewAttribute.PushTab,
            METADATA.PUSH_TAB,
        );
    };

    /**
     * Dispatches the Sign event.
     */
    private dispatchCaptureSignature = (): void => {
        this.dispatch(
            GemviewEvent.Sign,
            GemviewElement.Sign,
            GemviewAttribute.Sign,
            METADATA.SIGN_START,
        );
    };

    /**
     * Dispatches the IdleScreen event.
     */
    private dispatchIdleScreen = (): void => {
        this.dispatch(
            GemviewEvent.IdleScreen,
            GemviewElement.IdleScreen,
            GemviewAttribute.IdleScreen,
            METADATA.IDLE_SCREEN,
        );
    };

    /**
     * Checks if the DOM has a listener for the given GemviewResponse type.
     *
     * @param listener - The GemviewResponse listener type to check for.
     * @returns - True if a listener has already been added to the DOM, else false.
     */
    private hasListener = (listener: GemviewResponse): boolean => {
        const s = this.subscribers.find(s => s.type == listener);
        return !!s && !!s.subscriber;
    };

    /**
     * Adds the listener to the DOM with the given callback.
     *
     * @param listener - The GemviewResponse type to listen for.
     * @param subscriber - The rxjs subscriber to use for notifications.
     * @param callback - The callback function to execute when the event fires.
     */
    private addListener = (listener: GemviewResponse, subscriber: Subscriber<any>, callback: (e: Event) => void): void => {
        const s = this.subscribers.find(s => s.type == listener);

        if (!s) {
            this.subscribers.push({ type: listener, subscriber });
        } else {
            s.subscriber = subscriber;
        }

        document.documentElement.addEventListener(listener, callback, false);
    };

    /**
     * Removes the listener from the DOM.
     *
     * @param listener - The GemviewResponse type listener to remove.
     * @param callback - The callback function to remove.
     */
    private removeListener = (listener: GemviewResponse, callback: (e: Event) => void): void => {
        const s = this.subscribers.find(s => s.type == listener);

        if (s) {
            s.subscriber = null;
            document.documentElement.removeEventListener(listener, callback, false);
        }
    };

    /**
     * Gets the current subscriber for the given listener.
     *
     * @param listener - The GemviewResponse type to get the subscriber for.
     * @returns - The requested subscriber.
     */
    private getSubscriber = (listener: GemviewResponse): Subscriber<any> => {
        const s = this.subscribers.find(s => s.type == listener);

        if (s) {
            return s.subscriber;
        }

        // This should never occur, but just in case.
        throw new Error(`No subscriber for ${listener}`);
    };

    /**
     * Subscribes to the gemview messages.
     *
     * @param type The message type to subscribe to.
     * @param next - The callback to invoke on success.
     * @param error - The callback to invoke on error.
     */
    public subscribeToMessage = <T>(
        type: GemviewMessageType,
        next: (result: GemviewMessage<T>) => void,
        error: (error: any) => void = null
    ): void => {
        this.gemviewMessagingService.subscribe(type, next, error);
    };

    /**
     * Sends a message with an empty reponse.
     *
     * @param type The message type to send.
     */
    public sendEmptyMessage = (type: GemviewMessageType): void => {
        this.gemviewMessagingService.send(null, type);
    };

    /**
     * Disables right clicks in the component.
     */
    public disableRightClick = (): void => {
        document.addEventListener('contextmenu', (e) => e.preventDefault(), false);
    };

    /**
     * Handles responses from the DeviceStatus event.
     *
     * Potential device statuses:
     * 0 = No device detected
     * 1 = Topaz device
     * 2 = Gemview device
     * -1 = Error detecting the device
     * -2 = SigPlusExtLite is not installed or an old version is installed
     * -3 = SigPlus drivers are not installed
     * -4 = an old version of the SigPlus drivers are installed
     *
     * @param subscriber - The subscriber to emit values back to.
     * @param event - The DOM event containing the response.
     */
    private handleDeviceStatusResponse = (subscriber: Subscriber<boolean>, event: Event): void => {
        const element = event.target as HTMLElement;
        const response = JSON.parse(element.getAttribute(GemviewAttribute.Msg));

        element.remove();
        console.log(`Gemview device status is ${response.deviceStatus}`);

        switch (response.deviceStatus) {
            case 0:
            case 1:
                // Emit false to attempt to use SignatureGem.
                subscriber.next(false);
                subscriber.complete();
                break;

            case 2:
                subscriber.next(true);
                subscriber.complete();
                break;

            default:
                const message = response.deviceStatus < 0
                    ? `Gemview device detection error: ${response.errorMsg}`
                    : 'Gemview device not detected.';

                subscriber.error(new Error(message));
                break;
        }

        this.removeListener(GemviewResponse.DeviceStatus, this.deviceStatusResponseWrapper);
    };

    /**
     * Handles responses from the CustomWindow event.
     *
     * @param subscriber - The subscriber to emit values back to.
     * @param event - The DOM event containing the response.
     */
    private handleCustomWindowResponse = (subscriber: Subscriber<boolean>, event: Event): void => {
        const element = event.target as HTMLElement;
        const response = JSON.parse(element.getAttribute(GemviewAttribute.Msg));
        element.remove();

        if (response.status === 1) {
            subscriber.next(true);
            subscriber.complete();
        } else {
            const message = response.errorMsg ? `Error: ${response.errorMsg}` : `Status: ${response.deviceStatus}`;
            subscriber.error(new Error(`Failed to set up custom signing window. ${message}`));
        }

        this.removeListener(GemviewResponse.CustomWindow, this.customWindowResponseWrapper);
    };

    /**
     * Handles responses from the SignResponse event.
     *
     * @param subscriber - The subscriber to emit values back to.
     * @param event - The DOM Event containing the response.
     */
    private handleSignResponse = (subscriber: Subscriber<GemviewSignResponse>, event: Event): void => {
        var element = event.target as HTMLElement;
        var response: GemviewSignResponse = JSON.parse(element.getAttribute(GemviewAttribute.Msg));
        element.remove();

        if (!response.errorMsg) {
            subscriber.next(response);
            subscriber.complete();
        } else {
            subscriber.error(new Error(response.errorMsg));
        }

        this.removeListener(GemviewResponse.Sign, this.signResponseWrapper);
    };

    /**
     * Wrapper around the device status response handler.
     * Used for easy addition or removal of the event listener.
     *
     * @param event - The DOM event containing the response.
     */
    private deviceStatusResponseWrapper = (event: Event): void => {
        const subscriber = this.getSubscriber(GemviewResponse.DeviceStatus);
        this.handleDeviceStatusResponse(subscriber, event);

        // Cleanup duplicated DOM elements
        this.cleanupDeviceStatusResponse();
    };

    /**
     * Wrapper around the custom window response handler.
     * Used for easy addition or removal of the event listener.
     *
     * @param event - The DOM event containing the response.
     */
    private customWindowResponseWrapper = (event: Event): void => {
        const subscriber = this.getSubscriber(GemviewResponse.CustomWindow);
        this.handleCustomWindowResponse(subscriber, event);
    };

    /**
     * Wrapper around the sign response handler.
     * Used for easy addition or removal of the event listener.
     *
     * @param event - The DOM event containing the response.
     */
    private signResponseWrapper = (event: Event): void => {
        const subscriber = this.getSubscriber(GemviewResponse.Sign);
        this.handleSignResponse(subscriber, event);
    };

    /**
     * Cleans up DOM elements which aren't handled already.
     * This issue only occurs when we call to device status.
     */
    private cleanupDeviceStatusResponse = (): void => {
        // For some reason the device status HTML tags pile up, so we manually clean them up here.
        const deviceStatusTags = document.getElementsByTagName(GemviewElement.DeviceStatus);
        for (let i = 0; i < deviceStatusTags.length; i++) {
            try {
                const element = deviceStatusTags[i];
                element.parentNode.removeChild(element);
            } catch (e) {
                // Ignoring exceptions here.
            }
        }

        // Remove rogue version info response elements which seem to get sent when we check device status
        // but only in the photo first modal component.
        const dataTags = document.getElementsByTagName(GemviewElement.Data);
        for (let i = 0; i < dataTags.length; i++) {
            try {
                const element = dataTags[i];
                const data = JSON.parse(element.getAttribute(GemviewAttribute.Msg));

                if (data.spelVersion) {
                    // This is a version response tag, remove it.
                    element.parentNode.removeChild(element);
                }
            } catch (e) {
                // Ignoring exceptions here.
            }
        }
    };

    /**
     * Checks if the Gemview device is connecting and working.
     *
     * @returns - An observable that emits a single boolean indicating whether the device is connected and working.
     */
    public isGemviewReady = (): Observable<boolean> => new Observable((subscriber) => {
        if (!document.documentElement.getAttribute(GemviewAttribute.ExtensionInstalled)) {
            // When extension is not installed emit false to attempt to use SignatureGem.
            console.log('SigPlusExtLite extension is disabled or not installed.');
            subscriber.next(false);
            return subscriber.complete();
        }

        if (!this.hasListener(GemviewResponse.DeviceStatus)) {
            this.addListener(GemviewResponse.DeviceStatus, subscriber, this.deviceStatusResponseWrapper);
        }

        this.dispatchGetDeviceStatus();
    });

    /**
     * Alters the UI for the signing window to display correctly on the tablet.
     *
     * @returns - An observable that emits a single boolean indicating if the window was altered successfully.
     */
    public customizeSigningWindow = (): Observable<boolean> => new Observable((subscriber) => {
        if (!this.hasListener(GemviewResponse.CustomWindow)) {
            this.addListener(GemviewResponse.CustomWindow, subscriber, this.customWindowResponseWrapper);
        }

        this.dispatchCustomSigningWindow();
    });

    /**
     * Pushes the currently open browser tab to the tablet.
     *
     * @returns - An observable that emits nothing and completes once the tab is pushed.
     */
    public pushCurrentTab = (): Observable<void> => new Observable((subscriber) => {
        try {
            this.dispatchPushCurrentTab();
        } catch (e) {
            return subscriber.error(new Error(e));
        }

        subscriber.next();
        subscriber.complete();
    });

    /**
     * Initiates the signature window and listens for the response.
     *
     * @returns - An observable that emits the GemviewSignResponse once the signature is captured.
     */
    public captureSignature = (): Observable<GemviewSignResponse> => new Observable((subscriber) => {
        if (!this.hasListener(GemviewResponse.Sign)) {
            this.addListener(GemviewResponse.Sign, subscriber, this.signResponseWrapper);
        }

        this.dispatchCaptureSignature();
    });

    /**
     * Loads the idle screen on the tablet.
     *
     * @returns - An observable that emits nothing and completes once the idle screen is loaded.
     */
    public loadIdleScreen = (): Observable<void> => new Observable((subscriber) => {
        try {
            this.dispatchIdleScreen();
        } catch (e) {
            return subscriber.error(new Error(e));
        }

        subscriber.next();
        subscriber.complete();
    });

    /**
     * Closes the open window on the tablet, and opens the idle screen.
     */
    public idleTabletWindow = (): void => {
        this.gemviewMessagingService.send(null, GemviewMessageType.IdleTablet);
    };

    /**
     * Opens the window on the tablet and adds event listeners to the DOM for storage handling.
     *
     * @param uri - The URI of the page to open on the tablet.
     */
    protected openWindow = (uri: string): void => {
        try {
            this.tabletWindow = window.open(uri, '_blank');

            if (!this.tabletWindow) {
                return console.error('Failed to open tablet window.');
            }
        } catch (e) {
            console.error(e);
        }
    };

    /**
     * Handles the tablet response.
     *
     * @param message - The gemview message.
     * @param callback - The callback to invoke on the message.
     */
    public handleTabletResponse = <T>(message: GemviewMessage<T>, callback: (message: GemviewMessage<T>) => void): void => {
        if (message.data) {
            this.idleTabletWindow();
        }

        callback(message);
    };
}
